<template>
  <div id="app">
    <a class="skip-link" href="#app-body">Skip to content</a>

    <header class="app-header">
      <div class="container-fluid app-header-inner">
        <router-link to="/" exact class="app-brand">
          <b-icon icon="house-door" class="app-nav-icon" aria-hidden="true"></b-icon>
          <span>Home Ops</span>
        </router-link>

        <!-- Desktop (>= 1360px): the whole menu on one line -->
        <nav v-if="isAuthenticated" class="app-nav-desktop" aria-label="Main">
          <ul class="app-nav-list app-nav-group">
            <li v-for="link in operationLinks" :key="link.to">
              <b-dropdown
                v-if="link.children"
                variant="link"
                no-flip
                split
                :split-to="link.to"
                :split-class="['app-nav-link', 'text-decoration-none', { 'router-link-active': isChildActive(link) }]"
                toggle-class="app-nav-link text-decoration-none px-1"
                :title="link.title"
              >
                <template #button-content>
                  <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                  <span>{{link.label}}</span>
                </template>
                <b-dropdown-item
                  v-for="child in link.children"
                  :key="child.to"
                  :to="child.to"
                  link-class="app-nav-dropdown-item"
                >
                  <b-icon :icon="child.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                  <span>{{child.label}}</span>
                </b-dropdown-item>
              </b-dropdown>
              <router-link v-else :to="link.to" class="app-nav-link" :title="link.title">
                <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>{{link.label}}</span>
              </router-link>
            </li>
          </ul>

          <ul class="app-nav-list app-nav-group">
            <li v-for="link in customerLinks" :key="link.to">
              <router-link :to="link.to" class="app-nav-link" :title="link.title">
                <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>{{link.label}}</span>
              </router-link>
            </li>
          </ul>

          <ul class="app-nav-list app-nav-group">
            <li v-for="link in resourceLinks" :key="link.to || link.label">
              <b-dropdown
                v-if="link.children"
                variant="link"
                no-flip
                :toggle-class="['app-nav-link', 'text-decoration-none', { 'router-link-active': isChildActive(link) }]"
                :title="link.title"
              >
                <template #button-content>
                  <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                  <span>{{link.label}}</span>
                </template>
                <b-dropdown-item
                  v-for="child in link.children"
                  :key="child.to"
                  :to="child.to"
                  link-class="app-nav-dropdown-item"
                >
                  <b-icon :icon="child.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                  <span>{{child.label}}</span>
                </b-dropdown-item>
              </b-dropdown>
              <router-link v-else :to="link.to" class="app-nav-link" :title="link.title">
                <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>{{link.label}}</span>
              </router-link>
            </li>
          </ul>

          <ul class="app-nav-list app-nav-group app-nav-end">
            <li v-if="userFullName" class="app-nav-user">{{userFullName}}</li>
            <li>
              <router-link to="/logout" class="app-nav-link" title="Logout">
                <b-icon icon="box-arrow-right" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>Logout</span>
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- Phone and tablet (< 1360px): one button opening the full screen menu -->
        <button
          v-if="isAuthenticated"
          ref="menuToggle"
          type="button"
          class="app-nav-toggle"
          aria-controls="app-menu"
          :aria-expanded="isMenuOpen ? 'true' : 'false'"
          @click="toggleMenu"
        >
          <b-icon icon="list" aria-hidden="true"></b-icon>
          <span>Menu</span>
        </button>

        <router-link v-else to="/login" class="app-nav-link app-nav-login" title="Login">
          <b-icon icon="box-arrow-in-right" class="app-nav-icon" aria-hidden="true"></b-icon>
          <span>Login</span>
        </router-link>
      </div>
    </header>

    <!-- Full screen menu: same links, same order, same groups as the
         desktop bar. Closed by the button, by Escape, by a click outside
         the links, and as soon as the user has navigated somewhere. -->
    <div
      v-if="isAuthenticated"
      id="app-menu"
      ref="menu"
      class="app-menu"
      :class="{ 'is-open': isMenuOpen }"
      :aria-hidden="isMenuOpen ? 'false' : 'true'"
      role="dialog"
      aria-modal="true"
      aria-label="Main menu"
      @keydown="onMenuKeydown"
      @click.self="closeMenu"
    >
      <div class="container-fluid app-menu-header">
        <p class="app-menu-title">Menu</p>
        <button
          ref="menuClose"
          type="button"
          class="app-menu-close"
          aria-label="Close the menu"
          @click="closeMenu"
        >
          <b-icon icon="x" aria-hidden="true"></b-icon>
        </button>
      </div>

      <nav class="container-fluid app-menu-body" aria-label="Main" @click.self="closeMenu">
        <ul class="app-nav-list app-nav-group">
          <template v-for="link in operationLinks">
            <li :key="link.to">
              <router-link :to="link.to" exact class="app-nav-link" :title="link.title">
                <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>{{link.label}}</span>
              </router-link>
            </li>
            <li v-for="child in link.children || []" :key="child.to">
              <router-link :to="child.to" class="app-nav-link" :title="child.label">
                <b-icon :icon="child.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>{{child.label}}</span>
              </router-link>
            </li>
          </template>
        </ul>

        <ul class="app-nav-list app-nav-group">
          <li v-for="link in customerLinks" :key="link.to">
            <router-link :to="link.to" class="app-nav-link" :title="link.title">
              <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
              <span>{{link.label}}</span>
            </router-link>
          </li>
        </ul>

        <ul class="app-nav-list app-nav-group">
          <template v-for="(link, index) in resourceLinks">
            <template v-if="link.children">
              <li class="app-nav-user app-menu-heading" :key="link.label">{{link.label}}</li>
              <li v-for="child in link.children" :key="child.to">
                <router-link :to="child.to" class="app-nav-link" :title="child.label">
                  <b-icon :icon="child.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                  <span>{{child.label}}</span>
                </router-link>
              </li>
            </template>
            <li
              v-else
              :key="link.to"
              :class="{ 'app-menu-row-start': index > 0 && resourceLinks[index - 1].children }"
            >
              <router-link :to="link.to" class="app-nav-link" :title="link.title">
                <b-icon :icon="link.icon" class="app-nav-icon" aria-hidden="true"></b-icon>
                <span>{{link.label}}</span>
              </router-link>
            </li>
          </template>
        </ul>

        <ul class="app-nav-list app-nav-group">
          <li v-if="userFullName" class="app-nav-user">{{userFullName}}</li>
          <li>
            <router-link to="/logout" class="app-nav-link" title="Logout">
              <b-icon icon="box-arrow-right" class="app-nav-icon" aria-hidden="true"></b-icon>
              <span>Logout</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </div>

    <main id="app-body" tabindex="-1">
      <globalErrorModalComponent />
      <LoadingComponent />
      <router-view/>
    </main>
  </div>
</template>

<script>
import globalErrorModalComponent from '@/components/globalErrorModalComponent'
import LoadingComponent from '@/components/LoadingComponent'

// Same value as the `min-width: 1360px` breakpoint of the design system:
// above it the whole menu fits on one line and the sheet does not exist
const DESKTOP_MENU_QUERY = '(min-width: 1360px)'

export default {
  name: 'App',
  components : {
    globalErrorModalComponent,
    LoadingComponent
  },
  data: function () {
    return {
      isMenuOpen: false,
      // Element that had the focus before the menu was opened, so it can
      // be given back when the menu closes
      previouslyFocused: null,
      desktopQuery: null,
      operationLinks: [
        { to: '/clients', label: 'Customers', title: 'Customers', icon: 'people' },
        {
          to: '/mission-requests', label: 'Mission Requests', title: 'Mission Requests', icon: 'inbox',
          children: [
            { to: '/empty-legs', label: 'Empty legs', icon: 'airplane' },
          ],
        },
        { to: '/legs', label: 'Upcoming Missions', title: 'Upcoming Missions', icon: 'calendar-week' },
      ],
      customerLinks: [
        { to: '/jetcards', label: 'Jet Cards', title: 'Jet Cards', icon: 'wallet2' },
        { to: '/mobility', label: 'Mobility Cards', title: 'Mobility Cards', icon: 'credit-card' },
      ],
      resourceLinks: [
        {
          label: 'Providers',
          title: 'Providers',
          icon: 'briefcase',
          children: [
            { to: '/fleet/aircrafts', label: 'Aircraft', icon: 'cursor' },
            { to: '/fleet/operators', label: 'Operators', icon: 'building' },
            { to: '/service-providers', label: 'Services', icon: 'tools' },
          ],
        },
        { to: '/intelligence-base', label: 'Intelligence', title: 'Intelligence Base', icon: 'book' },
        { to: '/ai_agents', label: 'AI Agents', title: 'AI Agents', icon: 'cpu' },
        {
          label: 'Statistics',
          title: 'Statistics',
          icon: 'graph-up',
          children: [
            { to: '/statistics', label: 'Statistics', icon: 'bar-chart-line' },
            { to: '/fleet/outreach', label: 'Operator outreach', icon: 'megaphone' },
          ],
        },
      ],
    }
  },
  computed: {
    isAuthenticated () {
      return this.$store.getters.isAuthenticated
    },
    token () {
      return this.$store.getters.TOKEN
    },
    userFirstName () {
      return this.$store.getters.getUserFirstName
    },
    userLastName () {
      return this.$store.getters.getUserLastName
    },
    // Empty when the name is unknown, so the header shows nothing at all
    // rather than a placeholder built from missing values
    userFullName () {
      return [this.userFirstName, this.userLastName].filter(Boolean).join(' ')
    },
  },
  mounted () {
    // Going from the phone layout to the desktop one hides the sheet:
    // release the body and forget the open state
    if (window.matchMedia) {
      this.desktopQuery = window.matchMedia(DESKTOP_MENU_QUERY)
      this.onDesktopChange(this.desktopQuery)
      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener('change', this.onDesktopChange)
      } else {
        this.desktopQuery.addListener(this.onDesktopChange)
      }
    }
  },
  beforeDestroy () {
    document.body.classList.remove('has-menu-open')
    if (this.desktopQuery) {
      if (this.desktopQuery.removeEventListener) {
        this.desktopQuery.removeEventListener('change', this.onDesktopChange)
      } else {
        this.desktopQuery.removeListener(this.onDesktopChange)
      }
    }
  },
  methods: {
    isChildActive (link) {
      return link.children.some((child) => this.$route.path.startsWith(child.to))
    },
    toggleMenu () {
      if (this.isMenuOpen) {
        this.closeMenu()
      } else {
        this.openMenu()
      }
    },
    openMenu () {
      this.previouslyFocused = document.activeElement
      this.isMenuOpen = true
    },
    closeMenu () {
      this.isMenuOpen = false
    },
    onDesktopChange (event) {
      if (event.matches) {
        this.isMenuOpen = false
      }
    },
    // Every element of the sheet the keyboard can reach, in DOM order
    menuFocusables () {
      if (!this.$refs.menu) {
        return []
      }
      return Array.from(
        this.$refs.menu.querySelectorAll('a[href], button:not([disabled])')
      )
    },
    onMenuKeydown (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.closeMenu()
        return
      }
      if (event.key !== 'Tab') {
        return
      }
      // Focus trap: Tab cycles inside the sheet and never behind it
      const focusables = this.menuFocusables()
      if (!focusables.length) {
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
  },
  watch: {
    isMenuOpen (isOpen) {
      // The page behind the sheet must not scroll
      document.body.classList.toggle('has-menu-open', isOpen)
      if (isOpen) {
        this.$nextTick(() => {
          const target = this.$refs.menuClose || this.menuFocusables()[0]
          if (target) {
            target.focus()
          }
        })
        return
      }
      // Give the focus back where the user left it
      const target = this.previouslyFocused || this.$refs.menuToggle
      if (target && document.body.contains(target)) {
        target.focus()
      }
      this.previouslyFocused = null
    },
    // Close the menu once the user has navigated somewhere
    $route () {
      this.isMenuOpen = false
    }
  }
}
</script>

<style>
/* The header, the menu and every shared component are described by the
   design system (src/assets/design/), so that a page cannot restyle them
   on its own. Only what is strictly local to the shell lives here. */

.app-nav-login {
  margin-left: auto;
}
</style>
